import { useCallback, useEffect, useRef, useState } from 'react';
import Modal from './Modal.jsx';
import Avatar from './Avatar.jsx';
import Waveform from './Waveform.jsx';
import AudioNote from './AudioNote.jsx';
import { api } from '../api.js';
import {
  canRecord, mediaUrl, prepareImage, startRecording, uploadMedia,
} from '../lib/upload.js';
import { clockLabel } from '../lib/audio.js';
import {
  insertMention, matchingFriends, mentionAtCaret, tagsIn,
} from '../lib/mentions.js';

const BODY_MAX = 200;
const COUNTER_FROM = 140;
const MAX_MEDIA = 4;

// Optional, and most posts carry none. A mode is not what a post is; it is a
// flag saying something could be done about this — so it stays behind a
// toolbar button rather than sitting in everyone's way.
const MODES = [
  ['offering', 'Offering'],
  ['looking', 'Looking for'],
  ['happening', 'Happening'],
];

const toLocalInput = (ms) => {
  if (!Number.isFinite(ms)) return '';
  return new Date(ms - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};


/* Toolbar glyphs are inline SVG, not emoji: emoji fall back to tofu on any
   system without an emoji font, which is most Linux desktops. */
const Icon = ({ path, filled = false }) => (
  <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true"
    fill={filled ? 'currentColor' : 'none'} stroke="currentColor"
    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {path}
  </svg>
);

const ICON_PHOTO = (
  <>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <circle cx="8.5" cy="9.5" r="1.6" />
    <path d="M21 16l-5.5-5L7 20" />
  </>
);
const ICON_MIC = (
  <>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <path d="M12 18v3" />
  </>
);
const ICON_STOP = <rect x="6" y="6" width="12" height="12" rx="2" />;
const ICON_FLAG = (
  <>
    <path d="M5 21V4" />
    <path d="M5 5h11l-1.5 3.5L16 12H5z" />
  </>
);

export default function PostComposer({
  me, initialPost = null, livePosts = [], onEditPost, onClose, onPosted, onChanged,
  doing = null, onDoingChanged, friends = [],
}) {
  const editing = initialPost;
  const [body, setBody] = useState(editing?.body ?? '');
  const [mode, setMode] = useState(editing?.mode ?? null);
  const [startsAt, setStartsAt] = useState(toLocalInput(editing?.startsAt));
  const [media, setMedia] = useState(editing?.media ?? []);
  const [showMode, setShowMode] = useState(!!editing?.mode);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [levels, setLevels] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const [activeMention, setActiveMention] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);

  const fileRef = useRef(null);
  const textRef = useRef(null);
  const [doingBusy, setDoingBusy] = useState(false);

  const mentionMatches = matchingFriends(friends, activeMention);

  const updateMention = (value, caret) => {
    setActiveMention(mentionAtCaret(value, caret));
    setMentionIndex(0);
  };

  const acceptMention = (friend) => {
    if (!activeMention || !friend?.username) return;
    const next = insertMention(body, activeMention, friend.username, BODY_MAX);
    setBody(next.text);
    setActiveMention(null);
    setMentionIndex(0);
    requestAnimationFrame(() => {
      textRef.current?.focus();
      textRef.current?.setSelectionRange(next.caret, next.caret);
      grow();
    });
  };

  // What you are doing and what you are posting are the same question asked
  // at two lengths, so they belong in one place rather than behind two
  // buttons that both said "tell us something".
  const usualTags = [...new Set([...(me?.interests ?? []), 'walking', 'coffee', 'reading'])].slice(0, 6);

  async function setDoing(tag) {
    if (doingBusy) return;
    setDoingBusy(true);
    try {
      if (doing?.tag === tag) {
        await api.clearDoing();
        onDoingChanged?.(null);
      } else {
        const { doing: next } = await api.createDoing({ tag });
        onDoingChanged?.(next);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDoingBusy(false);
    }
  }

  // The text is the composer. It grows with what you write rather than
  // scrolling inside a fixed box.
  const grow = useCallback(() => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 260)}px`;
  }, []);

  useEffect(() => { grow(); }, [grow]);

  // Drive the moving bars and the timer from one animation loop.
  useEffect(() => {
    if (!recording) return undefined;
    const started = Date.now();
    let frame = 0;
    let last = 0;
    const tick = (now) => {
      frame = requestAnimationFrame(tick);
      setElapsed(Date.now() - started);
      if (now - last < 55) return;       // ~18 bars a second reads as speech
      last = now;
      setLevels((prev) => [...prev, recording.level()].slice(-240));
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [recording]);

  // Closing the composer mid-recording must hand the microphone back, or the
  // browser goes on showing the recording indicator with nothing running.
  const recordingRef = useRef(null);
  recordingRef.current = recording;
  useEffect(() => () => recordingRef.current?.cancel?.(), []);

  const ready = (body.trim().length > 0 || media.length > 0)
    && (mode !== 'happening' || !!startsAt)
    && !busy;

  const addImage = useCallback(async (file) => {
    if (!file || media.length >= MAX_MEDIA) return;
    setError('');
    setBusy(true);
    try {
      const { blob, width, height } = await prepareImage(file);
      const saved = await uploadMedia(blob, { width, height });
      setMedia((current) => [...current, saved].slice(0, MAX_MEDIA));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [media.length]);

  async function toggleRecording() {
    setError('');
    if (recording) {
      setBusy(true);
      try {
        const { blob, durationMs, peaks } = await recording.stop();
        setRecording(null);
        setLevels([]);
        const saved = await uploadMedia(blob, { durationMs, peaks });
        setMedia((current) => [...current, saved].slice(0, MAX_MEDIA));
      } catch (err) {
        setError(err.message);
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      setElapsed(0);
      setLevels([]);
      setRecording(await startRecording());
    } catch {
      setError('No microphone available, or permission was declined.');
    }
  }

  async function submit() {
    if (!ready) return;
    setBusy(true);
    setError('');
    try {
      const payload = {
        mode,
        body: body.trim(),
        // Written into the sentence, not collected beside it.
        tags: tagsIn(body),
        // The full ordered list is the edit contract. Existing ids are kept,
        // omitted ids are removed, and newly uploaded ids land in this order.
        mediaIds: media.map((m) => m.id),
        startsAt: mode === 'happening' && startsAt ? new Date(startsAt).getTime() : null,
      };
      const { post } = editing
        ? await api.editPost(editing.id, payload)
        : await api.createPost(payload);
      (editing ? onChanged : onPosted)?.(post);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  // Paste a screenshot, drop a photo — both attach without a file dialog.
  const onPaste = (event) => {
    const file = [...(event.clipboardData?.files ?? [])][0];
    if (file?.type.startsWith('image/')) { event.preventDefault(); addImage(file); }
  };
  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    const file = [...(event.dataTransfer?.files ?? [])].find((f) => f.type.startsWith('image/'));
    if (file) addImage(file);
  };

  const remaining = BODY_MAX - body.length;

  return (
    <Modal onClose={onClose}>
      <div
        className={`composer ${dragging ? 'dragging' : ''}`}
        onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <div className="composer-top">
          <Avatar person={me} size="lg" />

          <div className="composer-main">
            {!editing && usualTags.length > 0 && (
              <div className="composer-doing">
                <span className="composer-doing-label">Right now</span>
                {usualTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className={`chip-btn ${doing?.tag === tag ? 'on' : ''}`}
                    disabled={doingBusy}
                    onClick={() => setDoing(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}

            <textarea
              ref={textRef}
              className="composer-text"
              value={body}
              maxLength={BODY_MAX}
              placeholder="What's going on nearby? Use @ for people and places, # for tags."
              aria-label="What's going on nearby?"
              aria-autocomplete="list"
              aria-controls={mentionMatches.length ? 'post-mention-suggestions' : undefined}
              onChange={(event) => {
                setBody(event.target.value);
                updateMention(event.target.value, event.target.selectionStart);
                grow();
              }}
              onClick={(event) => updateMention(event.currentTarget.value, event.currentTarget.selectionStart)}
              onKeyUp={(event) => {
                if (!['Tab', 'ArrowDown', 'ArrowUp', 'Escape'].includes(event.key)) {
                  updateMention(event.currentTarget.value, event.currentTarget.selectionStart);
                }
              }}
              onPaste={onPaste}
              onKeyDown={(event) => {
                if (mentionMatches.length && event.key === 'Tab') {
                  event.preventDefault();
                  acceptMention(mentionMatches[mentionIndex] ?? mentionMatches[0]);
                  return;
                }
                if (mentionMatches.length && event.key === 'ArrowDown') {
                  event.preventDefault();
                  setMentionIndex((index) => (index + 1) % mentionMatches.length);
                  return;
                }
                if (mentionMatches.length && event.key === 'ArrowUp') {
                  event.preventDefault();
                  setMentionIndex((index) => (index - 1 + mentionMatches.length) % mentionMatches.length);
                  return;
                }
                if (activeMention && event.key === 'Escape') {
                  event.preventDefault();
                  setActiveMention(null);
                  return;
                }
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') submit();
              }}
            />

            {mentionMatches.length > 0 && (
              <div id="post-mention-suggestions" className="mention-popover" role="listbox">
                {mentionMatches.map((friend, index) => (
                  <button
                    key={friend.id ?? friend.actorId}
                    type="button"
                    role="option"
                    aria-selected={index === mentionIndex}
                    className={`mention-option ${index === mentionIndex ? 'selected' : ''}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => acceptMention(friend)}
                  >
                    <Avatar person={friend} size="sm" />
                    <span><b>{friend.displayName}</b><small>@{friend.username}</small></span>
                    {index === mentionIndex && <kbd>Tab</kbd>}
                  </button>
                ))}
              </div>
            )}

            {recording && (
              <div className="recording-strip">
                <span className="rec-dot" aria-hidden="true" />
                <Waveform peaks={levels} live height={30} />
                <span className="rec-elapsed">{clockLabel(elapsed)}</span>
                <button
                  type="button" className="rec-action stop"
                  onClick={toggleRecording}
                >
                  Stop
                </button>
                <button
                  type="button" className="rec-action"
                  onClick={() => { recording.cancel(); setRecording(null); setLevels([]); }}
                >
                  Cancel
                </button>
              </div>
            )}

            {media.length > 0 && (
              <div className="composer-media">
                {media.map((item, index) => (
                  <div key={item.id} className={`composer-thumb ${item.kind}`}>
                    {item.kind === 'image'
                      ? <img src={mediaUrl(item.id)} alt="" />
                      : (
                        <AudioNote
                          src={mediaUrl(item.id)}
                          peaks={item.peaks}
                          durationMs={item.durationMs}
                          compact
                          download={false}
                        />
                      )}
                    <button
                      type="button"
                      className="thumb-remove"
                      aria-label="Remove"
                      onClick={() => setMedia((c) => c.filter((m) => m.id !== item.id))}
                    >
                      ×
                    </button>
                    {media.length > 1 && (
                      <span className="thumb-order">
                        <button
                          type="button" disabled={index === 0} aria-label="Move earlier"
                          onClick={() => setMedia((current) => {
                            const next = current.slice();
                            [next[index - 1], next[index]] = [next[index], next[index - 1]];
                            return next;
                          })}
                        >‹</button>
                        <button
                          type="button" disabled={index === media.length - 1} aria-label="Move later"
                          onClick={() => setMedia((current) => {
                            const next = current.slice();
                            [next[index], next[index + 1]] = [next[index + 1], next[index]];
                            return next;
                          })}
                        >›</button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {showMode && (
              <div className="composer-extra">
                <div className="mode-picker">
                  {MODES.map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`chip-btn ${mode === value ? 'on' : ''}`}
                      aria-pressed={mode === value}
                      onClick={() => setMode(mode === value ? null : value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {mode === 'happening' && (
                  <input
                    type="datetime-local"
                    className="composer-when"
                    aria-label="When"
                    value={startsAt}
                    onChange={(event) => setStartsAt(event.target.value)}
                  />
                )}
              </div>
            )}


            {error && <p className="form-error">{error}</p>}
          </div>
        </div>

        {!editing && livePosts.some((post) => post.status === 'live' && post.currentUntil > Date.now()) && (
          <div className="composer-current-posts">
            <div className="field-label">What you’re carrying now</div>
            {livePosts.filter((post) => post.status === 'live' && post.currentUntil > Date.now()).slice(0, 12).map((post) => (
              <button key={post.id} type="button" className="current-post-row" onClick={() => onEditPost?.(post)}>
                <span>{post.body || `${post.media.length} media item${post.media.length === 1 ? '' : 's'}`}</span>
                <span>Edit →</span>
              </button>
            ))}
          </div>
        )}

        <div className="composer-bar">
          <div className="composer-tools">
            <button
              type="button" className="tool" title="Add a photo"
              autoFocus
              disabled={busy || media.length >= MAX_MEDIA}
              onClick={() => fileRef.current?.click()}
            >
              <Icon path={ICON_PHOTO} /><span className="sr-only">Add a photo</span>
            </button>
            <button
              type="button"
              className={`tool ${recording ? 'recording' : ''}`}
              title={recording ? 'Stop recording' : 'Record a voice note'}
              disabled={(busy && !recording) || !canRecord() || media.length >= MAX_MEDIA}
              onClick={toggleRecording}
            >
              <Icon path={recording ? ICON_STOP : ICON_MIC} filled={!!recording} />
              <span className="sr-only">{recording ? 'Stop recording' : 'Record a voice note'}</span>
            </button>
            <button
              type="button" className={`tool ${showMode ? 'on' : ''}`}
              title="Is anything being asked?"
              onClick={() => setShowMode((v) => !v)}
            >
              <Icon path={ICON_FLAG} /><span className="sr-only">Is anything being asked?</span>
            </button>
            <input
              ref={fileRef} type="file" accept="image/*" capture="environment" hidden
              onChange={(event) => addImage(event.target.files?.[0])}
            />
          </div>

          <div className="composer-submit">
            {body.length >= COUNTER_FROM && (
              <span className={`composer-count ${remaining <= 20 ? 'low' : ''}`}>{remaining}</span>
            )}
            <button type="button" className="btn btn-primary" disabled={!ready} onClick={submit}>
              {busy ? '…' : editing ? 'Save' : 'Post'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
